<template>
  <div class="page-container">
    <Sidebar />
    <div class="main-content">
      <div class="yaoxie-page">
        <div class="page-header">
          <h1>食药同源管理</h1>
          <div class="header-actions">
            <button class="add-btn" @click="openBatchModal">+ 新建批次</button>
            <button class="import-btn" @click="triggerImport" :disabled="!selectedBatch">
              <span>📥 导入Excel</span>
            </button>
            <input type="file" ref="fileInput" @change="handleFileUpload" accept=".xlsx,.xls" style="display: none" />
          </div>
        </div>

        <!-- 批次选择 -->
        <div class="batch-selector">
          <label>选择批次：</label>
          <select v-model="selectedBatch" @change="loadItems" class="batch-select">
            <option value="">全部品种</option>
            <option v-for="batch in batches" :key="batch.id" :value="batch.id">
              {{ batch.batch_name }} ({{ batch.item_count }}条)
            </option>
          </select>
          <button v-if="selectedBatch" class="delete-batch-btn" @click="deleteBatch">删除批次</button>
        </div>

        <!-- 搜索 -->
        <div class="filter-bar">
          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="搜索品种名称..."
            @keyup.enter="loadItems"
          />
          <button class="search-btn" @click="loadItems">搜索</button>
        </div>

        <!-- 数据列表 -->
        <div class="content-body">
          <div class="loading" v-if="loading">加载中...</div>
          <div class="error" v-else-if="error">{{ error }}</div>

          <div class="table-container" v-else>
            <table class="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>名称</th>
                  <th>拼音</th>
                  <th>分类</th>
                  <th>部位</th>
                  <th>功能</th>
                  <th>批次</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in items" :key="item.id">
                  <td>{{ item.id }}</td>
                  <td>{{ item.name }}</td>
                  <td>{{ item.pinyin || '-' }}</td>
                  <td>{{ item.category || '-' }}</td>
                  <td>{{ item.part || '-' }}</td>
                  <td class="function-cell">{{ item.function || '-' }}</td>
                  <td>{{ item.batch_name || '-' }}</td>
                  <td class="actions">
                    <button class="edit-btn" @click="editItem(item)">编辑</button>
                    <button class="delete-btn" @click="deleteItem(item.id)">删除</button>
                  </td>
                </tr>
                <tr v-if="items.length === 0">
                  <td colspan="8" class="empty">暂无数据</td>
                </tr>
              </tbody>
            </table>

            <div class="pagination" v-if="totalPages > 1">
              <button :disabled="currentPage === 1" @click="changePage(currentPage - 1)">上一页</button>
              <span>{{ currentPage }} / {{ totalPages }}</span>
              <button :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">下一页</button>
            </div>
          </div>
        </div>

        <!-- 批次创建弹窗 -->
        <div class="modal" v-if="showBatchModal">
          <div class="modal-content">
            <h2>新建批次</h2>
            <form @submit.prevent="createBatch">
              <div class="form-group">
                <label>批次名称 *</label>
                <input type="text" v-model="batchForm.batch_name" required placeholder="例如：第一批食药同源品种" />
              </div>
              <div class="form-group">
                <label>发布日期</label>
                <input type="text" v-model="batchForm.publish_date" placeholder="例如：2024-01-01" />
              </div>
              <div class="form-group">
                <label>发布单位</label>
                <input type="text" v-model="batchForm.publisher" placeholder="例如：国家卫健委" />
              </div>
              <div class="form-group">
                <label>描述</label>
                <textarea v-model="batchForm.description" placeholder="批次描述"></textarea>
              </div>
              <div class="form-actions">
                <button type="button" class="cancel-btn" @click="closeBatchModal">取消</button>
                <button type="submit" class="submit-btn">创建</button>
              </div>
            </form>
          </div>
        </div>

        <!-- 品种编辑弹窗 -->
        <div class="modal" v-if="showItemModal">
          <div class="modal-content">
            <h2>编辑品种</h2>
            <form @submit.prevent="updateItem">
              <div class="form-group">
                <label>名称 *</label>
                <input type="text" v-model="itemForm.name" required />
              </div>
              <div class="form-group">
                <label>拼音</label>
                <input type="text" v-model="itemForm.pinyin" />
              </div>
              <div class="form-group">
                <label>分类</label>
                <input type="text" v-model="itemForm.category" />
              </div>
              <div class="form-group">
                <label>部位</label>
                <input type="text" v-model="itemForm.part" />
              </div>
              <div class="form-group">
                <label>功能</label>
                <textarea v-model="itemForm.function"></textarea>
              </div>
              <div class="form-group">
                <label>用法</label>
                <textarea v-model="itemForm.usage"></textarea>
              </div>
              <div class="form-group">
                <label>注意事项</label>
                <textarea v-model="itemForm.notice"></textarea>
              </div>
              <div class="form-group">
                <label>拉丁名</label>
                <input type="text" v-model="itemForm.latin_name" />
              </div>
              <div class="form-group">
                <label>适用人群</label>
                <textarea v-model="itemForm.applicable_people"></textarea>
              </div>
              <div class="form-actions">
                <button type="button" class="cancel-btn" @click="closeItemModal">取消</button>
                <button type="submit" class="submit-btn">保存</button>
              </div>
            </form>
          </div>
        </div>

        <!-- 导入进度弹窗 -->
        <div class="modal" v-if="showImportModal">
          <div class="modal-content">
            <h2>导入结果</h2>
            <div class="import-result">
              <p>{{ importResult.message }}</p>
              <p v-if="importResult.count">成功导入 {{ importResult.count }} 条数据</p>
            </div>
            <div class="form-actions">
              <button class="submit-btn" @click="closeImportModal">确定</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Sidebar from '../components/Sidebar.vue'
import { getAdminPassword } from '../utils/api'

export default {
  name: 'YaoxieList',
  components: { Sidebar },
  data() {
    return {
      batches: [],
      selectedBatch: '',
      items: [],
      loading: false,
      error: '',
      searchQuery: '',
      currentPage: 1,
      pageSize: 20,
      total: 0,
      showBatchModal: false,
      showItemModal: false,
      showImportModal: false,
      batchForm: { batch_name: '', publish_date: '', publisher: '', description: '' },
      itemForm: {},
      editItemId: null,
      importResult: { message: '', count: 0 }
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    }
  },
  mounted() {
    this.loadBatches()
    this.loadItems()
  },
  methods: {
    async loadBatches() {
      try {
        const response = await axios.get('/api/admin/yaoxie/batches', {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data.code === 200) {
          this.batches = response.data.data.list || []
        }
      } catch (err) {
        console.error('Load batches error:', err)
      }
    },
    async loadItems() {
      this.loading = true
      this.error = ''
      try {
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize,
          search: this.searchQuery || undefined,
          batch_id: this.selectedBatch || undefined
        }
        const response = await axios.get('/api/admin/yaoxie/items', {
          params,
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data.code === 200) {
          const data = response.data.data
          this.items = data.list || []
          this.total = data.total || 0
        }
      } catch (err) {
        this.error = '获取数据失败'
      } finally {
        this.loading = false
      }
    },
    changePage(page) {
      this.currentPage = page
      this.loadItems()
    },
    openBatchModal() {
      this.batchForm = { batch_name: '', publish_date: '', publisher: '', description: '' }
      this.showBatchModal = true
    },
    closeBatchModal() {
      this.showBatchModal = false
    },
    async createBatch() {
      try {
        const response = await axios.post('/api/admin/yaoxie/batches', this.batchForm, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data.code === 200) {
          this.closeBatchModal()
          this.loadBatches()
          alert('批次创建成功')
        } else {
          alert(response.data.msg || '创建失败')
        }
      } catch (err) {
        alert('创建失败')
      }
    },
    async deleteBatch() {
      if (!this.selectedBatch) return
      if (!confirm('确定删除该批次？批次下的所有品种也会被删除！')) return
      try {
        const response = await axios.delete(`/api/admin/yaoxie/batches/${this.selectedBatch}`, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data.code === 200) {
          this.selectedBatch = ''
          this.loadBatches()
          this.loadItems()
          alert('批次删除成功')
        } else {
          alert(response.data.msg || '删除失败')
        }
      } catch (err) {
        alert('删除失败')
      }
    },
    editItem(item) {
      this.editItemId = item.id
      this.itemForm = {
        name: item.name || '',
        pinyin: item.pinyin || '',
        category: item.category || '',
        part: item.part || '',
        function: item.function || '',
        usage: item.usage || '',
        notice: item.notice || '',
        latin_name: item.latin_name || '',
        applicable_people: item.applicable_people || ''
      }
      this.showItemModal = true
    },
    closeItemModal() {
      this.showItemModal = false
      this.editItemId = null
    },
    async updateItem() {
      try {
        const response = await axios.put(`/api/admin/yaoxie/items/${this.editItemId}`, this.itemForm, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data.code === 200) {
          this.closeItemModal()
          this.loadItems()
          alert('更新成功')
        } else {
          alert(response.data.msg || '更新失败')
        }
      } catch (err) {
        alert('更新失败')
      }
    },
    async deleteItem(id) {
      if (!confirm('确定删除该品种？')) return
      try {
        const response = await axios.delete(`/api/admin/yaoxie/items/${id}`, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data.code === 200) {
          this.loadItems()
          this.loadBatches()
        } else {
          alert(response.data.msg || '删除失败')
        }
      } catch (err) {
        alert('删除失败')
      }
    },
    triggerImport() {
      if (!this.selectedBatch) {
        alert('请先选择批次')
        return
      }
      this.$refs.fileInput.click()
    },
    async handleFileUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      const formData = new FormData()
      formData.append('file', file)
      formData.append('batch_id', this.selectedBatch)

      try {
        const response = await axios.post('/api/admin/yaoxie/items/import', formData, {
          headers: {
            'x-admin-password': getAdminPassword(),
            'Content-Type': 'multipart/form-data'
          }
        })
        if (response.data.code === 200) {
          this.importResult = {
            message: response.data.msg,
            count: response.data.data?.count || 0
          }
          this.showImportModal = true
          this.loadItems()
          this.loadBatches()
        } else {
          alert(response.data.msg || '导入失败')
        }
      } catch (err) {
        alert('导入失败: ' + (err.response?.data?.msg || err.message))
      }

      // 清空文件输入
      event.target.value = ''
    },
    closeImportModal() {
      this.showImportModal = false
    }
  }
}
</script>

<style scoped>
.page-container {
  display: flex;
  min-height: 100vh;
}
.main-content {
  margin-left: 260px;
  flex: 1;
  padding: 30px;
  background: #f5f5f5;
}

.yaoxie-page { }
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.page-header h1 { font-size: 24px; color: #2c3e50; }
.header-actions { display: flex; gap: 10px; }
.add-btn, .import-btn { padding: 10px 20px; background: #3498db; color: white; border: none; border-radius: 5px; cursor: pointer; }
.import-btn { background: #27ae60; }
.import-btn:disabled { background: #bdc3c7; cursor: not-allowed; }

.batch-selector { display: flex; align-items: center; gap: 10px; margin-bottom: 20px; }
.batch-selector label { color: #2c3e50; font-weight: bold; }
.batch-select { padding: 8px 15px; border: 1px solid #ddd; border-radius: 5px; min-width: 200px; }
.delete-batch-btn { padding: 8px 15px; background: #e74c3c; color: white; border: none; border-radius: 5px; cursor: pointer; }

.filter-bar { display: flex; gap: 10px; margin-bottom: 20px; }
.search-input { padding: 8px 15px; border: 1px solid #ddd; border-radius: 5px; flex: 1; min-width: 200px; }
.search-btn { padding: 8px 20px; background: #3498db; color: white; border: none; border-radius: 5px; cursor: pointer; }

.content-body { background: white; padding: 20px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
.loading, .error, .empty { text-align: center; padding: 50px; color: #666; }
.table-container { overflow-x: auto; }
.data-table { width: 100%; border-collapse: collapse; }
.data-table th, .data-table td { padding: 12px 15px; text-align: left; border-bottom: 1px solid #eee; }
.data-table th { background: #f8f9fa; font-weight: bold; color: #2c3e50; }
.data-table tr:hover { background: #f5f5f5; }
.function-cell { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.actions { display: flex; gap: 10px; }
.edit-btn, .delete-btn { padding: 6px 12px; border: none; border-radius: 5px; cursor: pointer; font-size: 12px; }
.edit-btn { background: #3498db; color: white; }
.delete-btn { background: #e74c3c; color: white; }

.pagination { display: flex; justify-content: center; align-items: center; gap: 15px; margin-top: 20px; }
.pagination button { padding: 8px 15px; background: white; border: 1px solid #ddd; border-radius: 5px; cursor: pointer; }
.pagination button:disabled { opacity: 0.5; cursor: not-allowed; }

.modal { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; justify-content: center; align-items: center; z-index: 1000; }
.modal-content { background: white; padding: 30px; border-radius: 10px; width: 500px; max-height: 90vh; overflow-y: auto; }
.modal-content h2 { margin: 0 0 20px; }
.form-group { margin-bottom: 15px; }
.form-group label { display: block; margin-bottom: 5px; color: #666; }
.form-group input, .form-group select, .form-group textarea { width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px; }
.form-group textarea { min-height: 80px; resize: vertical; }
.form-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
.cancel-btn, .submit-btn { padding: 10px 20px; border: none; border-radius: 5px; cursor: pointer; }
.cancel-btn { background: #ddd; }
.submit-btn { background: #3498db; color: white; }

.import-result { text-align: center; padding: 20px; }
.import-result p { margin: 10px 0; color: #2c3e50; }
</style>
